import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import Button from "./Button";
import { preventOrphan, formatProductTitle, formatSlogan, splitDesktopTitle } from '../utils/text/textFormatting';
import { toUrlFriendly } from "../utils/urlHelpers";

const shimmerStyle = {
  animation: "shimmer 2s infinite linear",
  background: "linear-gradient(to right, #e0e0e0 8%, #f0f0f0 18%, #e0e0e0 33%)",
  backgroundSize: "1000px 100%",
};

function SkeletonBlock({ className, style }) {
  return (
    <div
      className={`${className} relative overflow-hidden bg-[#A9AAAC] rounded-md`}
      style={{ ...shimmerStyle, ...style }}
    />
  );
}

const DesktopProductCard = ({ product, sequence = 0, isProfessional = false }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isTextLoaded, setIsTextLoaded] = useState(false);
  const [localProduct, setLocalProduct] = useState(null);
  
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  const hasProduct = product && product.fields;

  useEffect(() => {
    if (hasProduct) {
      setLocalProduct(product);
    }
  }, [hasProduct, product]);

  useEffect(() => {
    if (localProduct && localProduct.fields) {
      setIsTextLoaded(true);
    }
  }, [localProduct]);


  const { sys } = localProduct || {};
  const {
    productTitle,
    slogan,
    productHeroImage,
    productImageFallback,
    price,
    buyNowButtonUrl,
    detailedProductDescription,
  } = (localProduct && localProduct.fields) || {};

  console.log('Product data:', {
    isProfessional,
    isTextLoaded,
    buyNowButtonUrl,
    price
  });

  console.log('Slogan before transform:', slogan);

  const formatSloganToTitleCase = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  console.log('Slogan after transform:', formatSloganToTitleCase(slogan));

  const imageUrl = productHeroImage?.fields?.file?.url || null;
  const detailHref = `/${isProfessional ? 'industrial' : 'household'}/product/${isProfessional ? 'industrial' : 'household'}/${toUrlFriendly(productTitle)}`;
  const isComingSoon = buyNowButtonUrl === "Coming Soon!";
  

  // Animation variants for sequential animations
  const textVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const formatTitleForTwoLines = (title) => {
    const words = title.split(' ');
    if (words.length <= 2) {
      // For short titles, add line break after first word
      return words.join('\n');
    } else {
      // For longer titles, try to balance the lines
      const midPoint = Math.ceil(words.length / 2);
      return words.slice(0, midPoint).join(' ') + '\n' + words.slice(midPoint).join(' ');
    }
  };

  if (!hasProduct) {
    return (
      <div className="relative w-full h-auto min-h-[450px]">
        <div className="w-full h-full bg-white rounded-3xl shadow-md p-3">
          <SkeletonBlock className="w-full h-72 mb-2" />
          <SkeletonBlock className="w-48 h-6 mb-2" />
          <SkeletonBlock className="w-56 h-4 mb-2" />
          <SkeletonBlock className="w-24 h-5 mb-2" />
          <SkeletonBlock className="w-32 h-8 mb-1" />
          <SkeletonBlock className="w-32 h-8" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-auto min-h-[450px]"
    >
      <div className="w-full relative">
        {/* PRODUCT CARD */}
        <div className="w-full bg-white rounded-3xl shadow-md p-3">
          <AnimatePresence>
            {(!isImageLoaded || !isTextLoaded) && (
              <motion.div
                key="skeleton"
                className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-3xl"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
              >
                {!isImageLoaded && (
                  <SkeletonBlock className="w-full h-72 mb-2" />
                )}
                {!isTextLoaded && (
                  <>
                    <SkeletonBlock className="w-48 h-6 mb-2" />
                    <SkeletonBlock className="w-56 h-4 mb-2" />
                    <SkeletonBlock className="w-24 h-5 mb-2" />
                    <SkeletonBlock className="w-32 h-8 mb-1" />
                    <SkeletonBlock className="w-32 h-8" />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isImageLoaded && isTextLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col"
          >
            {/* Image Container */}
            <div className="w-full flex justify-center mb-2">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={productTitle || "Product Image"}
                  className="w-48 h-48 object-cover rounded-lg"
                  onLoad={() => setIsImageLoaded(true)}
                  onError={(e) => {
                    if (productImageFallback && e.currentTarget.src !== productImageFallback) {
                      e.currentTarget.src = productImageFallback;
                      return;
                    }
                    setIsImageLoaded(true);
                  }}
                />
              )}
            </div>

            {/* Content Container */}
            <div className="flex flex-col items-center px-2">
              <div className="flex flex-col items-center w-full">
                {isTextLoaded && productTitle && (
                  <h3
                    className="text-[1.4rem] font-bold text-gray-900 tracking-wide leading-tight text-center min-h-[3.2rem]"
                    style={{
                      fontFamily: "Geogrotesque, sans-serif",
                      textTransform: "uppercase",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      whiteSpace: "pre-line",
                      lineHeight: "1.6rem",
                      width: "100%"
                    }}
                  >
                    {(() => {
                      const [l1, l2] = splitDesktopTitle(productTitle);
                      return `${l1}\n${l2}`;
                    })()}
                  </h3>
                )}

                {/* Slogan */}
                {isTextLoaded && slogan && (
                  <motion.p
                    initial="hidden"
                    animate="visible"
                    variants={textVariants}
                    className="text-base italic text-gray-700 mt-1 text-center"
                    style={{ 
                      fontFamily: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      lineHeight: "1.2",
                      width: "100%",
                      minHeight: "2.4em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "clamp(0.65rem, 1.5vw, 0.85rem)",
                      WebkitFontSmoothing: "antialiased",
                      MozOsxFontSmoothing: "grayscale"
                    }}
                  >
                    {formatSloganToTitleCase(slogan)}
                  </motion.p>
                )}

                {isTextLoaded && typeof price === "number" && !isProfessional && (
                  <p
                    className="text-lg font-bold text-gray-900 mt-2"
                    style={{ fontFamily: "Geogrotesque, sans-serif" }}
                  >
                    ${price.toFixed(2)}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-4 mx-auto w-full mt-4">
                {!isProfessional && isTextLoaded && buyNowButtonUrl && (
                  <Button
                    to={isComingSoon ? detailHref : undefined}
                    href={!isComingSoon ? buyNowButtonUrl : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="medium"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isComingSoon) {
                        window.open(buyNowButtonUrl, '_blank');
                      }
                    }}
                  >
                    {isComingSoon ? "View Details" : "Buy Now"}
                  </Button>
                )}
                {isTextLoaded && sys?.id && (
                  <a
                    href={detailHref}
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Button
                      variant={isProfessional ? "primary" : "secondary"}
                      size="medium"
                      fullWidth
                    >
                      More Details
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DesktopProductCard; 